import type { ChecklistAnswerItemResponse } from "@/api/model"

// Shared types for the checklist / dropdown subtypes.
export type ChecklistAnswerType = 'SingleChoice' | 'MultipleChoice' | 'MultipleChoiceWithDecline'

export type ChecklistAnswerItem  = ChecklistAnswerItemResponse;

/**
 * A checklist item while it is being edited. `editorId` is a client-only, stable row identity:
 * unsaved items all carry `id: null`, which is unusable as a sortable/React key once rows can be
 * dragged. It never leaves the editor — `checklistItemsToModel` strips it again on save. Same
 * idea as `SubtypeState.editorId`.
 */
export type EditorChecklistAnswerItem = ChecklistAnswerItem & { editorId: number }

export function isChecklistAnswerItemArray(value: unknown): value is ChecklistAnswerItem[] {
  return Array.isArray(value) && value.every(v => typeof v === 'object' && v !== null && 'text' in v)
}

export function isEditorChecklistAnswerItemArray(value: unknown): value is EditorChecklistAnswerItem[] {
  return isChecklistAnswerItemArray(value) && value.every(v => 'editorId' in v)
}

/**
 * API → editor state: attach the row identity. The items arrive in their persisted order (the
 * backend sorts by `order`), so the array position is authoritative from here on.
 */
export function checklistItemsToEditorState(value: unknown): EditorChecklistAnswerItem[] {
  if (!isChecklistAnswerItemArray(value)) return []
  return value.map((item, index) => ({ ...item, editorId: index }))
}

/**
 * Editor state → API: the array position *is* the order, so `order` is renumbered from it and the
 * client-only `editorId` is dropped. Same normalization the subtypes themselves get in
 * `ActionModuleEditor.buildRequest`.
 */
export function checklistItemsToModel(value: unknown): ChecklistAnswerItem[] {
  if (!isChecklistAnswerItemArray(value)) return []
  return value.map((item, index) => {
    const { editorId: _editorId, ...model } = item as EditorChecklistAnswerItem
    return { ...model, order: index }
  })
}

/** Next free row identity, for an item added in the editor. */
export function nextChecklistItemEditorId(items: EditorChecklistAnswerItem[]): number {
  return items.reduce((max, item) => Math.max(max, item.editorId), -1) + 1
}
